import { isAuthorized, RoleTest, usePermissions } from '@luminovo/auth';
import { isPresent } from '@luminovo/commons';
import * as React from 'react';
import type { JSX } from 'react';

import { isAuthorizedForModule, ModulePermissionTest } from './isAuthorizedForModule';
import { useIsAdvancedRoleManagementEnabled, useModulePermissions } from './useModulePermissions';

interface Props {
    /**
     * A list of all the required permissions. The children will only be
     * rendered if all the required permissions are met.
     */
    requiredPermissions: RoleTest;
    /**
     * Same as above, but for the advanced access management defined in spicedb
     * Only takes effect if is_advanced_role_management_enabled feature flag is set.
     * If not supplied, `requiredPermissions` will be respected regardless of the feature flag
     */
    requiredModulePermissions?: ModulePermissionTest;
    /**
     * Use this as fallback content in case not all the required permissions
     * are met.
     */
    fallback?: JSX.Element;
}

/**
 * This component will only render its children when the `requiredPermissions` are met.
 */
export function Protected({
    requiredPermissions,
    requiredModulePermissions,
    fallback,
    children,
}: React.PropsWithChildren<Props>): JSX.Element | null {
    const isAdvancedRoleManagementEnabled = useIsAdvancedRoleManagementEnabled();

    const modulePermissions = useModulePermissions();

    const { permissions } = usePermissions();

    const success =
        isPresent(requiredModulePermissions) && isAdvancedRoleManagementEnabled
            ? isAuthorizedForModule(modulePermissions, requiredModulePermissions)
            : isAuthorized(permissions, requiredPermissions);

    if (!success) {
        return fallback ?? null;
    }

    return <>{children}</>;
}
